/**
 * Continuity of a Registry commitment (migration 983; PRD-assignment-record.md §3.5, D1).
 *
 * A commitment keeps its identity while its assignments change, until a person rules
 * that what follows is a different undertaking. This section shows the lineage
 * (Supersedes / Superseded by / Merged from), the standing ruling with who made it, when
 * and why, and three acts: "Rule on continuity", "Supersede" and "Merge" (1024). The
 * ruling is a person's, never computed; the three continuity questions are prompts in the
 * ceremony. Supersede and Merge are drawn only when a "different" ruling stands from this
 * person or they are an admin, Merge only when another commitment stands the same way,
 * and each takes a disposition for every open assignment (canon 14: nothing carries
 * silently). A control is drawn only for a person who may use it (build contract §2.8);
 * the server stays the authority.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, GitBranch, GitMerge, Plus, Scale, X } from 'lucide-react';
import { Modal, TextInput, TextArea, Select, Field, Checkbox } from '@/components/ds';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useOrgUnitNames } from '@/hooks/useOrgUnitNames';
import {
  CONTINUITY_PROMPTS,
  CONTINUITY_WORDS,
  SUPERSESSION_WORDS,
  assigneeLabel,
  holdsAssignmentAuthority,
  listAssignmentsForCommitment,
  mayRuleOnContinuity,
  maySupersede,
  mergeCandidates,
  mergeCommitments,
  ruleCommitmentContinuity,
  supersedeCommitment,
  supersessionActsFor,
  type AssignmentView,
  type ContinuityRuling,
  type SuccessorInput,
  type SupersessionDisposition,
} from '@/lib/assignmentService';
import type {
  CommitmentPriority,
  CommitmentType,
  CommitmentWithCoverage,
} from '@/types/prioritization/commitment';

interface Props {
  commitment: CommitmentWithCoverage;
  /** Every commitment of the workspace, to name the ones it is linked to. */
  allCommitments: CommitmentWithCoverage[];
  linkedItems: { id: string; title?: string | null }[];
  /** Open another commitment in the detail. */
  onOpen: (commitmentId: string) => void;
  /** After a ruling or a supersession; `select` names the commitment to open next. */
  onChanged: (next?: { select?: string }) => void | Promise<void>;
}

const TYPE_OPTIONS: { value: CommitmentType; label: string }[] = [
  { value: 'customer', label: 'Customer' },
  { value: 'partner', label: 'Partner' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'internal', label: 'Internal' },
];

const PRIORITY_OPTIONS: { value: CommitmentPriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function fmtAt(ts: string | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The lineage, at the top of the detail: "Superseded by <successor>" on a commitment that
 * was replaced (or split), "Supersedes <old>" on its successor. Nothing for a commitment
 * with no lineage.
 */
export function CommitmentLineage({ commitment, allCommitments, onOpen }: Pick<Props, 'commitment' | 'allCommitments' | 'onOpen'>) {
  // A merge's successor names no single predecessor, so a merged commitment names it instead.
  const successors = allCommitments.filter((c) => c.supersedesCommitmentId === commitment.id || c.id === commitment.supersededByCommitmentId);
  const mergedFrom = commitment.supersedesCommitmentId
    ? []
    : allCommitments.filter((c) => c.supersededByCommitmentId === commitment.id);
  const mergedInto = !!commitment.supersededByCommitmentId
    && allCommitments.filter((c) => c.supersededByCommitmentId === commitment.supersededByCommitmentId).length > 1;
  const predecessor = commitment.supersedesCommitmentId
    ? allCommitments.find((c) => c.id === commitment.supersedesCommitmentId) ?? null
    : null;
  const predecessorSplit = predecessor
    ? allCommitments.filter((c) => c.supersedesCommitmentId === predecessor.id).length > 1
    : false;
  const superseded = commitment.status === 'superseded';
  if (!superseded && !commitment.supersedesCommitmentId && mergedFrom.length === 0) return null;

  return (
    <div className="space-y-1.5 text-sm">
      {superseded && (
        <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
          <div className="text-[var(--text-pri)]">
            {mergedInto ? 'Merged into' : `Superseded${successors.length > 1 ? ', split into' : ' by'}`}{' '}
            {successors.length === 0 ? (
              <span className="text-[var(--text-ter)]">a commitment not shown here</span>
            ) : (
              successors.map((s, i) => (
                <span key={s.id}>
                  {i > 0 && ', '}
                  <button type="button" onClick={() => onOpen(s.id)} className="font-medium hover:underline">{s.title}</button>
                </span>
              ))
            )}
            {commitment.supersededAt && <span className="text-[var(--text-ter)]"> · {fmtAt(commitment.supersededAt)}</span>}
          </div>
          <div className="text-xs text-[var(--text-ter)] mt-0.5">
            Its assignments are history, kept as they ended. The successor carries the undertaking.
          </div>
        </div>
      )}
      {mergedFrom.length > 0 && (
        <div className="text-xs text-[var(--text-sec)]">
          <GitMerge className="inline h-3 w-3 mr-1 text-[var(--text-ter)]" />
          Merged from{' '}
          {mergedFrom.map((m, i) => (
            <span key={m.id}>
              {i > 0 && ', '}
              <button type="button" onClick={() => onOpen(m.id)} className="font-medium hover:underline">{m.title}</button>
            </span>
          ))}
        </div>
      )}
      {commitment.supersedesCommitmentId && (
        <div className="flex items-center gap-1 text-xs text-[var(--text-sec)]">
          <ArrowRight className="h-3 w-3 text-[var(--text-ter)]" />
          Supersedes{' '}
          {predecessor ? (
            <button type="button" onClick={() => onOpen(predecessor.id)} className="font-medium hover:underline">{predecessor.title}</button>
          ) : (
            <span className="text-[var(--text-ter)]">a commitment not shown here</span>
          )}
          {predecessorSplit && <span className="text-[var(--text-ter)]"> (one part of a split)</span>}
        </div>
      )}
    </div>
  );
}

/**
 * The standing continuity ruling and the two acts. Nothing for a commitment with no ruling
 * when this person may not rule (a superseded one, or someone without the authority).
 */
export function ContinuitySection({ commitment, allCommitments, linkedItems, onChanged }: Omit<Props, 'onOpen'>) {
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const who = { mayAuthorize: holdsAssignmentAuthority(can), isAdmin: can('admin.settings'), userId };
  const { users } = useSystemUsers();
  const nameOf = (id: string | null) => {
    if (!id) return 'someone';
    if (id === userId) return 'you';
    const u = users.find((x) => x.id === id);
    return u ? (u.name || u.email) : 'someone';
  };

  const [ruling, setRuling] = useState(false);
  const [superseding, setSuperseding] = useState(false);
  const [merging, setMerging] = useState(false);

  const mayRule = mayRuleOnContinuity(commitment, who);
  const mayReplace = maySupersede(commitment, who);
  const candidates = mergeCandidates(commitment, allCommitments, who);
  const standing = commitment.continuityRuling;

  if (!standing && !mayRule) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Continuity</div>
        <div className="flex items-center gap-3">
          {mayRule && (
            <button
              type="button"
              onClick={() => setRuling(true)}
              className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)]"
            >
              <Scale className="h-3 w-3" /> {standing ? 'Rule again' : 'Rule on continuity'}
            </button>
          )}
          {mayReplace && (
            <button
              type="button"
              onClick={() => setSuperseding(true)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90"
            >
              <GitBranch className="h-3 w-3" /> Supersede
            </button>
          )}
          {candidates.length > 0 && (
            <button
              type="button"
              onClick={() => setMerging(true)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90"
            >
              <GitMerge className="h-3 w-3" /> Merge
            </button>
          )}
        </div>
      </div>

      <div className="space-y-1.5 text-sm">
        {standing && (
          <div className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
            <div className="text-[var(--text-pri)]">
              <span className="font-medium">{CONTINUITY_WORDS[standing].label}</span>
              <span className="text-[var(--text-ter)]">
                {' '}· ruled by {nameOf(commitment.continuityRuledBy)}{commitment.continuityRuledAt ? ` on ${fmtAt(commitment.continuityRuledAt)}` : ''}
              </span>
            </div>
            {commitment.continuityBasis && (
              <div className="text-xs text-[var(--text-sec)] mt-0.5"><span className="text-[var(--text-ter)]">Basis:</span> {commitment.continuityBasis}</div>
            )}
            <div className="text-xs text-[var(--text-ter)] mt-0.5">{CONTINUITY_WORDS[standing].meaning}</div>
            {standing === 'different' && !mayReplace && (
              <div className="text-xs text-[var(--text-ter)] mt-0.5">
                Superseding it takes the person who ruled ({nameOf(commitment.continuityRuledBy)}) or a workspace admin.
              </div>
            )}
            {mayReplace && candidates.length === 0 && (
              <div className="text-xs text-[var(--text-ter)] mt-0.5">
                To merge it with another commitment, rule that one a different undertaking too.
              </div>
            )}
          </div>
        )}
        {!standing && (
          <p className="text-xs text-[var(--text-ter)] italic">
            No ruling. This commitment keeps its identity while its assignments change; to replace it, rule first that what follows is a different undertaking.
          </p>
        )}
      </div>

      {ruling && (
        <RuleContinuityModal
          commitment={commitment}
          onClose={() => setRuling(false)}
          onDone={async () => { setRuling(false); await onChanged(); }}
        />
      )}
      {superseding && (
        <SupersedeModal
          commitment={commitment}
          linkedItems={linkedItems}
          onClose={() => setSuperseding(false)}
          onDone={async (select) => { setSuperseding(false); await onChanged({ select }); }}
        />
      )}
      {merging && (
        <MergeModal
          commitment={commitment}
          candidates={candidates}
          onClose={() => setMerging(false)}
          onDone={async (select) => { setMerging(false); await onChanged({ select }); }}
        />
      )}
    </section>
  );
}

// ── Rule on continuity ───────────────────────────────────────────────────────

function RuleContinuityModal({ commitment, onClose, onDone }: {
  commitment: CommitmentWithCoverage;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [choice, setChoice] = useState<ContinuityRuling | null>(null);
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!choice) return;
    setSaving(true); setErr(null);
    const res = await ruleCommitmentContinuity(commitment.id, choice, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Rule on continuity"
      description="Is what follows still this commitment, or a different one? You rule; nothing here decides it for you. The ruling is recorded as a decision, with your name and your reason."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
          <button
            type="button"
            disabled={!choice || !basis.trim() || saving}
            onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Recording…' : 'Record the ruling'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <div className="text-xs text-[var(--text-ter)] mb-1">Weigh these. They are questions, not a test.</div>
          <ul className="list-disc pl-5 text-xs text-[var(--text-sec)] space-y-0.5">
            {CONTINUITY_PROMPTS.map((q) => <li key={q}>{q}</li>)}
          </ul>
        </div>
        <Field label="Your ruling" required>
          <div className="grid grid-cols-2 gap-2">
            {(['same', 'different'] as ContinuityRuling[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setChoice(r)}
                aria-pressed={choice === r}
                className={`text-left px-3 py-2 border ${choice === r ? 'border-[var(--text-pri)]' : 'border-[var(--rule)]'}`}
              >
                <div className="text-sm font-medium text-[var(--text-pri)]">{CONTINUITY_WORDS[r].label}</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">{CONTINUITY_WORDS[r].meaning}</div>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Basis" required hint="Why the undertaking is, or is not, still the same one.">
          <TextArea rows={3} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="What changed, and why that does or does not make it a different undertaking" />
        </Field>
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}

// ── Supersede ─────────────────────────────────────────────────────────────────

interface SuccessorDraft {
  title: string;
  commitmentType: CommitmentType;
  counterparty: string;
  dueDate: string;
  ownerName: string;
  priority: CommitmentPriority;
  carryItems: boolean;
}

function draftFrom(c: CommitmentWithCoverage): SuccessorDraft {
  return {
    title: '',
    commitmentType: c.commitmentType,
    counterparty: c.counterparty ?? '',
    dueDate: c.dueDate ?? '',
    ownerName: c.ownerName ?? '',
    priority: c.priority,
    carryItems: true,
  };
}

/** A disposition choice as the select holds it: "release", "fulfill", "invalidate", or "transfer:<successor index>". */
function parseChoice(v: string): SupersessionDisposition | null {
  if (!v) return null;
  if (v.startsWith('transfer:')) return { act: 'transfer', successor: Number(v.slice('transfer:'.length)) };
  if (v === 'release' || v === 'fulfill' || v === 'invalidate') return { act: v };
  return null;
}

/** The successor's fields, for a supersede and a merge. */
function SuccessorFields({ draft, onPatch }: { draft: SuccessorDraft; onPatch: (patch: Partial<SuccessorDraft>) => void }) {
  return (
    <>
      <Field label="Title" required>
        <TextInput value={draft.title} onChange={(e) => onPatch({ title: e.target.value })} placeholder="What the new commitment is" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Type">
          <Select value={draft.commitmentType} onChange={(e) => onPatch({ commitmentType: e.target.value as CommitmentType })} options={TYPE_OPTIONS} />
        </Field>
        <Field label="Priority">
          <Select value={draft.priority} onChange={(e) => onPatch({ priority: e.target.value as CommitmentPriority })} options={PRIORITY_OPTIONS} />
        </Field>
        <Field label="Counterparty">
          <TextInput value={draft.counterparty} onChange={(e) => onPatch({ counterparty: e.target.value })} placeholder="Customer, partner or regulator" />
        </Field>
        <Field label="Due date">
          <TextInput type="date" value={draft.dueDate} onChange={(e) => onPatch({ dueDate: e.target.value })} />
        </Field>
        <Field label="Owner">
          <TextInput value={draft.ownerName} onChange={(e) => onPatch({ ownerName: e.target.value })} placeholder="Owner name" />
        </Field>
      </div>
    </>
  );
}

/** One open assignment and what becomes of it, for a supersede and a merge. */
function OpenAssignmentChoice({ row, who, subject, from, options, choice, onChoose }: {
  row: AssignmentView;
  who: string;
  subject: string;
  /** The commitment it sits on, when the list spans more than one. */
  from?: string;
  options: { value: string; label: string }[];
  choice: string;
  onChoose: (value: string) => void;
}) {
  if (!row.assignment_id) return null;
  const picked = parseChoice(choice);
  return (
    <div className="border border-[var(--rule)] px-3 py-2">
      {from && <div className="text-xs text-[var(--text-ter)] mb-0.5">On {from}</div>}
      <div className="text-sm text-[var(--text-pri)]">
        <span className="font-medium">{who}</span>
        <span className="text-[var(--text-sec)]"> {row.verb ? `will ${row.verb}` : 'will'} </span>
        <span>{subject}</span>
        <span className="text-xs text-[var(--text-ter)]"> · {row.is_binding ? 'binding' : row.disposition === 'suspended' ? 'suspended' : 'proposed'}</span>
      </div>
      <div className="mt-1.5">
        <Select value={choice} onChange={(e) => onChoose(e.target.value)} placeholder="What becomes of it" options={options} />
      </div>
      {picked && <div className="text-xs text-[var(--text-ter)] mt-1">{SUPERSESSION_WORDS[picked.act].meaning}</div>}
    </div>
  );
}

function SupersedeModal({ commitment, linkedItems, onClose, onDone }: {
  commitment: CommitmentWithCoverage;
  linkedItems: { id: string; title?: string | null }[];
  onClose: () => void;
  onDone: (select?: string) => void | Promise<void>;
}) {
  const userId = useUserStore((s) => s.userId);
  const { users } = useSystemUsers();
  const units = useOrgUnitNames(commitment.workspaceId);
  const names = useMemo(() => ({
    users: Object.fromEntries(users.map((u) => [u.id, u.name || u.email])),
    units: Object.fromEntries(units.map((u) => [u.id, u.name])),
  }), [users, units]);

  const [drafts, setDrafts] = useState<SuccessorDraft[]>(() => [draftFrom(commitment)]);
  const [open, setOpen] = useState<AssignmentView[] | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    listAssignmentsForCommitment(commitment.id)
      .then((rows) => { if (live) setOpen(rows.filter((r) => r.disposition === 'open' || r.disposition === 'suspended')); })
      .catch((e) => { if (live) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [commitment.id]);

  const split = drafts.length > 1;
  // A transfer names a successor by position; removing one clears the choices that named it or a later one.
  function removeDraft(i: number) {
    setDrafts((d) => d.filter((_, j) => j !== i));
    setChoices((c) => Object.fromEntries(Object.entries(c).filter(([, v]) => !v.startsWith('transfer:') || Number(v.slice(9)) < i)));
  }
  function patchDraft(i: number, patch: Partial<SuccessorDraft>) {
    setDrafts((d) => d.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  }

  const unnamed = (open ?? []).filter((r) => r.assignment_id && !parseChoice(choices[r.assignment_id] ?? '')).length;
  const titled = drafts.every((d) => d.title.trim().length > 0);
  const ready = open !== null && titled && unnamed === 0 && basis.trim().length > 0;

  async function submit() {
    setSaving(true); setErr(null);
    const successors: SuccessorInput[] = drafts.map((d) => ({
      title: d.title.trim(),
      commitmentType: d.commitmentType,
      counterparty: d.counterparty.trim() || null,
      dueDate: d.dueDate || null,
      ownerName: d.ownerName.trim() || null,
      priority: d.priority,
      itemIds: d.carryItems ? linkedItems.map((i) => i.id) : [],
    }));
    const dispositions: Record<string, SupersessionDisposition> = {};
    for (const r of open ?? []) {
      const d = r.assignment_id ? parseChoice(choices[r.assignment_id] ?? '') : null;
      if (r.assignment_id && d) dispositions[r.assignment_id] = d;
    }
    const res = await supersedeCommitment(commitment.id, successors, dispositions, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone(res.data.successor_commitment_ids[0]);
  }

  const successorName = (i: number) => drafts[i]?.title.trim() || (split ? `successor ${i + 1}` : 'the successor');

  return (
    <Modal
      open
      onClose={onClose}
      title={`Supersede "${commitment.title}"`}
      description="A new commitment takes this one's place; this one stays on the record, marked superseded. Say what becomes of each open assignment. A transferred assignment binds again only when its assignee accepts it."
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-[var(--text-ter)]">
            {open !== null && unnamed > 0
              ? `Name what becomes of ${unnamed === 1 ? 'the open assignment' : `each of the ${unnamed} open assignments`} still unnamed; nothing is carried silently.`
              : !titled ? `Give ${split ? 'every successor' : 'the successor'} a title.` : null}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
            <button
              type="button"
              disabled={!ready || saving}
              onClick={() => void submit()}
              className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
            >
              {saving ? 'Recording…' : split ? `Supersede, split in ${drafts.length}` : 'Supersede'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {drafts.map((d, i) => (
          <div key={i} className="border border-[var(--rule)] px-3 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                {split ? `Successor ${i + 1}` : 'The successor'}
              </div>
              {split && (
                <button type="button" onClick={() => removeDraft(i)} aria-label={`Remove successor ${i + 1}`}
                  className="text-[var(--text-ter)] hover:text-[var(--coral)]">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <SuccessorFields draft={d} onPatch={(patch) => patchDraft(i, patch)} />
            {linkedItems.length > 0 && (
              <Checkbox
                checked={d.carryItems}
                onChange={(e) => patchDraft(i, { carryItems: e.target.checked })}
                label={`Link the same ${linkedItems.length === 1 ? 'item' : `${linkedItems.length} items`} to this successor`}
              />
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => setDrafts((d) => [...d, draftFrom(commitment)])}
          className="inline-flex items-center gap-1 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          <Plus className="h-3 w-3" /> Add a successor (split this commitment)
        </button>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Open assignments</div>
          {open === null && !err && <p className="text-xs text-[var(--text-ter)]">Reading the assignments…</p>}
          {open !== null && open.length === 0 && (
            <p className="text-xs text-[var(--text-ter)] italic">No open assignment. Nothing is bound to carry over.</p>
          )}
          <div className="space-y-2">
            {(open ?? []).map((r) => (
              <OpenAssignmentChoice
                key={r.assignment_id ?? ''}
                row={r}
                who={r.assignee_user_id && r.assignee_user_id === userId ? 'You' : assigneeLabel(r, names)}
                subject={r.item_id ? (linkedItems.find((i) => i.id === r.item_id)?.title ?? 'an item') : (r.statement ?? '')}
                options={supersessionActsFor(r).flatMap((a) =>
                  a === 'transfer'
                    ? drafts.map((_, i) => ({ value: `transfer:${i}`, label: split ? `Transfer to ${successorName(i)}` : SUPERSESSION_WORDS.transfer.label }))
                    : [{ value: a, label: SUPERSESSION_WORDS[a].label }])}
                choice={choices[r.assignment_id ?? ''] ?? ''}
                onChoose={(v) => setChoices((c) => ({ ...c, [r.assignment_id as string]: v }))}
              />
            ))}
          </div>
        </div>

        <Field label="Basis" required hint="Recorded with the decision this writes, and on each assignment it ends or transfers.">
          <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Why a new undertaking takes this one's place" />
        </Field>
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}

// ── Merge (1024) ──────────────────────────────────────────────────────────────

function MergeModal({ commitment, candidates, onClose, onDone }: {
  commitment: CommitmentWithCoverage;
  /** The others this person may merge it with now. */
  candidates: CommitmentWithCoverage[];
  onClose: () => void;
  onDone: (select?: string) => void | Promise<void>;
}) {
  const userId = useUserStore((s) => s.userId);
  const { users } = useSystemUsers();
  const units = useOrgUnitNames(commitment.workspaceId);
  const names = useMemo(() => ({
    users: Object.fromEntries(users.map((u) => [u.id, u.name || u.email])),
    units: Object.fromEntries(units.map((u) => [u.id, u.name])),
  }), [users, units]);

  const [picked, setPicked] = useState<string[]>(() => (candidates.length === 1 ? [candidates[0].id] : []));
  const [draft, setDraft] = useState<SuccessorDraft>(() => draftFrom(commitment));
  const [open, setOpen] = useState<AssignmentView[] | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [basis, setBasis] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const merging = useMemo(() => [commitment.id, ...picked], [commitment.id, picked]);
  useEffect(() => {
    let live = true;
    setOpen(null);
    Promise.all(merging.map((id) => listAssignmentsForCommitment(id)))
      .then((lists) => { if (live) setOpen(lists.flat().filter((r) => r.disposition === 'open' || r.disposition === 'suspended')); })
      .catch((e) => { if (live) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [merging]);

  const titleOf = (id: string | null) => (id === commitment.id ? commitment.title : candidates.find((c) => c.id === id)?.title ?? 'a commitment');
  const unnamed = (open ?? []).filter((r) => r.assignment_id && !parseChoice(choices[r.assignment_id] ?? '')).length;
  const ready = picked.length > 0 && open !== null && draft.title.trim().length > 0 && unnamed === 0 && basis.trim().length > 0;

  async function submit() {
    setSaving(true); setErr(null);
    const dispositions: Record<string, SupersessionDisposition> = {};
    for (const r of open ?? []) {
      const d = r.assignment_id ? parseChoice(choices[r.assignment_id] ?? '') : null;
      if (r.assignment_id && d) dispositions[r.assignment_id] = { act: d.act };
    }
    const res = await mergeCommitments(merging, {
      title: draft.title.trim(),
      commitmentType: draft.commitmentType,
      counterparty: draft.counterparty.trim() || null,
      dueDate: draft.dueDate || null,
      ownerName: draft.ownerName.trim() || null,
      priority: draft.priority,
    }, dispositions, basis.trim());
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    await onDone(res.data.successor_commitment_id);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Merge "${commitment.title}"`}
      description="One new commitment takes the place of these; each stays on the record, marked merged. Say what becomes of every open assignment. A transferred assignment binds again only when its assignee accepts it, or the authority above them binds it."
      size="lg"
      footer={
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-xs text-[var(--text-ter)]">
            {picked.length === 0
              ? 'Choose what to merge it with.'
              : open !== null && unnamed > 0
                ? `Name what becomes of ${unnamed === 1 ? 'the open assignment' : `each of the ${unnamed} open assignments`} still unnamed; nothing is carried silently.`
                : !draft.title.trim() ? 'Give the new commitment a title.' : null}
          </div>
          <div className="flex items-center justify-end gap-2 flex-shrink-0">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
            <button
              type="button"
              disabled={!ready || saving}
              onClick={() => void submit()}
              className="text-xs px-3 py-1.5 whitespace-nowrap bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
            >
              {saving ? 'Recording…' : `Merge ${merging.length} into one`}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Merge with</div>
          <p className="text-xs text-[var(--text-ter)] mb-1.5">Only commitments ruled a different undertaking are listed.</p>
          <div className="space-y-1">
            {candidates.map((c) => (
              <Checkbox
                key={c.id}
                checked={picked.includes(c.id)}
                onChange={(e) => setPicked((p) => (e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id)))}
                label={c.title}
              />
            ))}
          </div>
        </div>

        <div className="border border-[var(--rule)] px-3 py-2 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">The new commitment</div>
          <SuccessorFields draft={draft} onPatch={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
          <p className="text-xs text-[var(--text-ter)]">It holds every item these commitments hold.</p>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">Open assignments</div>
          {open === null && !err && <p className="text-xs text-[var(--text-ter)]">Reading the assignments…</p>}
          {open !== null && open.length === 0 && (
            <p className="text-xs text-[var(--text-ter)] italic">No open assignment. Nothing is bound to carry over.</p>
          )}
          <div className="space-y-2">
            {(open ?? []).map((r) => (
              <OpenAssignmentChoice
                key={r.assignment_id ?? ''}
                row={r}
                who={r.assignee_user_id && r.assignee_user_id === userId ? 'You' : assigneeLabel(r, names)}
                subject={r.statement ?? 'an item'}
                from={titleOf(r.commitment_id)}
                options={supersessionActsFor(r).map((a) => ({
                  value: a === 'transfer' ? 'transfer:0' : a,
                  label: SUPERSESSION_WORDS[a].label,
                }))}
                choice={choices[r.assignment_id ?? ''] ?? ''}
                onChoose={(v) => setChoices((c) => ({ ...c, [r.assignment_id as string]: v }))}
              />
            ))}
          </div>
        </div>

        <Field label="Basis" required hint="Recorded with the decision this writes, and on each assignment it ends or transfers.">
          <TextArea rows={2} value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="Why one undertaking takes these commitments' place" />
        </Field>
        {err && <div className="text-xs text-[var(--coral)]">{err}</div>}
      </div>
    </Modal>
  );
}
